import { useState, useMemo, useCallback } from "react";
import dayjs from 'dayjs';
import { fetchData, HTTP_STATUS, Notifications, intl } from "dmsbasereact";
import { PatientStatusEnum } from "../../constants/PatientStatusEnum";
import { getGenderEnum } from "../../constants/GenderEnum";

export const useCreatePatientForm = ({ table, onClose, initialCpf, initialDob, initialName }) => {
    const [hasPregnancyPotential, setHasPregnancyPotential] = useState(false);
    const [menstrualCycleRegular, setMenstrualCycleRegular] = useState(false);
    const [hasResponsibleLegal, setHasResponsibleLegal] = useState(false);
    const [sameAddressAsPatient, setSameAddressAsPatient] = useState(false);
    const [showPregnancyPotencialModal, setShowPregnancyPotencialModal] = useState(false);
    const [cpfIsValid, setCpfIsValid] = useState(false);
    const [cpfValue, setCpfValue] = useState(initialCpf || '');
    const [selectedGenderOption, setSelectedGenderOption] = useState("");
    const [startDate, setStartDate] = useState(initialDob ? dayjs(initialDob) : null);
    const [createdPatientPk, setCreatedPatientPk] = useState(null);

    // Patient fields
    const [patientName, setPatientName] = useState(initialName || '');
    const [zipCode, setZipCode] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [address, setAddress] = useState('');
    const [addressNumber, setAddressNumber] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [rg, setRg] = useState('');
    const [rgIssuingAgency, setRgIssuingAgency] = useState('');

    // Responsible Legal states
    const [responsibleCpfIsValid, setResponsibleCpfIsValid] = useState(false);
    const [responsibleCpfValue, setResponsibleCpfValue] = useState('');
    const [responsibleBirthDate, setResponsibleBirthDate] = useState(null);
    const [responsibleName, setResponsibleName] = useState('');
    const [responsibleZipCode, setResponsibleZipCode] = useState('');
    const [responsibleCity, setResponsibleCity] = useState('');
    const [responsibleState, setResponsibleState] = useState('');
    const [responsibleAddress, setResponsibleAddress] = useState('');
    const [responsibleAddressNumber, setResponsibleAddressNumber] = useState('');
    const [responsiblePhone, setResponsiblePhone] = useState('');
    const [responsibleEmail, setResponsibleEmail] = useState('');
    const [responsibleRg, setResponsibleRg] = useState('');
    const [responsibleRgIssuingAgency, setResponsibleRgIssuingAgency] = useState('');

    // Contraceptive method for female patients with pregnancy potential
    const [selectedContraceptiveMethod, setSelectedContraceptiveMethod] = useState(null);
    const [selectedContraceptiveMethodName, setSelectedContraceptiveMethodName] = useState('');
    const [highlyEffectiveMethods, setHighlyEffectiveMethods] = useState([]);
    const [barrierMethods, setBarrierMethods] = useState([]);

    // Infertility reason for female patients without pregnancy potential
    const [selectedInfertilityReason, setSelectedInfertilityReason] = useState(undefined);
    const [selectedInfertilityReasonName, setSelectedInfertilityReasonName] = useState('');

    // Patient consent states (LGPD compliance)
    const [dataUsageConsentAccepted, setDataUsageConsentAccepted] = useState(false);
    const [contactAuthorization, setContactAuthorization] = useState(null); // null, true, or false

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const { isPatientFieldsValid, patientValidationErrors } = useMemo(() => {
        const errors = [];

        if (patientName.trim().length === 0) {
            errors.push({ field: "patientName", message: intl.get("PatientNameRequired").d("Patient name is required") });
        }
        if (!cpfIsValid) {
            errors.push({ field: "cpf", message: intl.get("PatientCpfRequired").d("Valid CPF is required") });
        }
        if (!startDate || !dayjs(startDate).isValid()) {
            errors.push({ field: "birthDate", message: intl.get("PatientBirthDateRequired").d("Birth date is required") });
        }
        if (selectedGenderOption === "") {
            errors.push({ field: "gender", message: intl.get("PatientGenderRequired").d("Gender is required") });
        }

        // Infertility reason required for female patients without pregnancy potential
        if (selectedGenderOption === getGenderEnum().F.value && !hasPregnancyPotential && !selectedInfertilityReason) {
errors.push({
                field: "selectedInfertilityReason",
                message: intl.get("InfertilityReasonRequired").d("Please select an infertility reason")
            });
        }

        // AR-009 Item 9.10: Validate at least one highly effective contraceptive method
        if (hasPregnancyPotential && highlyEffectiveMethods.length === 0) {
            errors.push({
                field: "highlyEffectiveMethods",
                message: intl.get("HighlyEffectiveMethodRequired").d("At least one highly effective contraceptive method is required")
            });
        }

        // AR-009 Item 9.11: Validate at least one barrier contraceptive method
        if (hasPregnancyPotential && barrierMethods.length === 0) {
            errors.push({
                field: "barrierMethods",
                message: intl.get("BarrierMethodRequired").d("At least one barrier contraceptive method is required")
            });
        }

        // Patient address validation (required fields)
        if (!zipCode?.trim()) {
            errors.push({
                field: "zipCode",
                message: intl.get("CepRequired").d("CEP is required")
            });
        }
        if (!city?.trim()) {
            errors.push({
                field: "city",
                message: intl.get("CityRequired").d("City is required")
            });
        }
        if (!state) {
            errors.push({
                field: "state",
                message: intl.get("StateRequired").d("State is required")
            });
        }
        if (!address?.trim()) {
            errors.push({
                field: "address",
                message: intl.get("AddressRequired").d("Address is required")
            });
        }
        if (!addressNumber?.trim()) {
            errors.push({
                field: "addressNumber",
                message: intl.get("NumberRequired").d("Number is required")
            });
        }

        // Patient consent validation (LGPD compliance)
        if (!dataUsageConsentAccepted) {
            errors.push({
                field: "dataUsageConsentAccepted",
                message: intl.get("DataUsageConsentRequired").d("Você deve aceitar os termos de coleta e uso de dados")
            });
        }
        if (contactAuthorization === null) {
            errors.push({
                field: "contactAuthorization",
                message: intl.get("ContactAuthorizationRequired").d("Você deve informar se o paciente autoriza ou não o recebimento de contato")
            });
        }

        return {
            isPatientFieldsValid: errors.length === 0,
            patientValidationErrors: errors,
        };
    }, [patientName, cpfIsValid, startDate, selectedGenderOption, hasPregnancyPotential, highlyEffectiveMethods, barrierMethods, selectedInfertilityReason, zipCode, city, state, address, addressNumber, dataUsageConsentAccepted, contactAuthorization]);

    const { isResponsibleFieldsValid, responsibleValidationErrors } = useMemo(() => {
        // AR-009 Item 9.13: Check if patient is under 18 years old
        const isMinor = startDate && dayjs(startDate).isValid()
            ? dayjs().diff(dayjs(startDate), 'year') < 18
            : false;

        // If patient is not a minor and user didn't check "has responsible legal", validation passes
        if (!isMinor && !hasResponsibleLegal) {
            return { isResponsibleFieldsValid: true, responsibleValidationErrors: [] };
        }

        const errors = [];

        // AR-009 Item 9.13: For minors, responsible legal is mandatory
        if (isMinor && !hasResponsibleLegal) {
            errors.push({
                field: "hasResponsibleLegal",
                message: intl.get("ResponsibleLegalRequiredForMinors").d("Responsible legal is required for patients under 18 years old")
            });
        }

        // If has responsible legal (either by user choice or because patient is minor), validate fields
        if (hasResponsibleLegal) {
            if (responsibleName.trim().length === 0) {
                errors.push({ field: "responsibleName", message: intl.get("ResponsibleNameRequired").d("Responsible legal name is required") });
            }
            if (!responsibleCpfIsValid) {
                errors.push({ field: "responsibleCpf", message: intl.get("ResponsibleCpfRequired").d("Responsible legal valid CPF is required") });
            }

            // Legal responsible address validation (when not using patient's address)
            if (!sameAddressAsPatient) {
                if (!responsibleZipCode?.trim()) {
                    errors.push({
                        field: "responsibleZipCode",
                        message: intl.get("ResponsibleCepRequired").d("CEP of Legal Responsible is required")
                    });
                }
                if (!responsibleCity?.trim()) {
                    errors.push({
                        field: "responsibleCity",
                        message: intl.get("ResponsibleCityRequired").d("City of Legal Responsible is required")
                    });
                }
                if (!responsibleState) {
                    errors.push({
                        field: "responsibleState",
                        message: intl.get("ResponsibleStateRequired").d("State of Legal Responsible is required")
                    });
                }
                if (!responsibleAddress?.trim()) {
                    errors.push({
                        field: "responsibleAddress",
                        message: intl.get("ResponsibleAddressRequired").d("Address of Legal Responsible is required")
                    });
                }
                if (!responsibleAddressNumber?.trim()) {
                    errors.push({
                        field: "responsibleAddressNumber",
                        message: intl.get("ResponsibleNumberRequired").d("Number of Legal Responsible is required")
                    });
                }
            }
        }

        return {
            isResponsibleFieldsValid: errors.length === 0,
            responsibleValidationErrors: errors,
        };
    }, [hasResponsibleLegal, responsibleName, responsibleCpfIsValid, startDate, sameAddressAsPatient, responsibleZipCode, responsibleCity, responsibleState, responsibleAddress, responsibleAddressNumber]);

    const isFormValid = useMemo(() => {
        return isPatientFieldsValid && isResponsibleFieldsValid;
    }, [isPatientFieldsValid, isResponsibleFieldsValid]);

    const validationErrors = useMemo(() => {
        return [...patientValidationErrors, ...responsibleValidationErrors];
    }, [patientValidationErrors, responsibleValidationErrors]);

    const fieldErrors = useMemo(() => {
        const errorMap = validationErrors.reduce((acc, err) => {
            acc[err.field] = err.message;
            return acc;
        }, {});
        return errorMap;
    }, [validationErrors]);

    const addPatientToDoctorList = async (patientPk) => {
        try {
            const response = await fetchData(
                "/doctor/add_patient",
                "POST",
                true,
                { patient_pk: patientPk }
            );
            return response.status === HTTP_STATUS.CREATED_201;
        } catch (error) {
            console.error("Error adding patient to doctor's list:", error);
            return false;
        }
    };

    const createResponsibleLegal = async (patientPk) => {
        try {
            const responsibleCpf = responsibleCpfValue.replace(/\D/g, "");
            const responsiblePhoneClean = responsiblePhone?.replace(/\D/g, "") || "";
            const responsibleBirthDay = responsibleBirthDate ? dayjs(responsibleBirthDate).startOf("day").toISOString() : null;

            const addressData = sameAddressAsPatient ? {
                address_name: "RL_" + responsibleCpf,
                zip_code: zipCode,
                city: city,
                state: state,
                address: address,
                number: addressNumber,
                country_code: 'BR',
            } : {
                address_name: "RL_" + responsibleCpf,
                zip_code: responsibleZipCode,
                city: responsibleCity,
                state: responsibleState,
                address: responsibleAddress,
                number: responsibleAddressNumber,
                country_code: 'BR',
            };

            const responsibleLegalData = {
                name: responsibleName,
                cpf: responsibleCpf,
                email: responsibleEmail,
                phone: responsiblePhoneClean,
                birth_date: responsibleBirthDay,
                rg: responsibleRg || null,
                rg_issuing_agency: responsibleRgIssuingAgency || null,
                address: addressData
            };

            const resCreateResponsibleLegal = await fetchData(
                "/responsible_legal",
                "POST",
                true,
                responsibleLegalData
            );

            if (resCreateResponsibleLegal.status === HTTP_STATUS.CREATED_201) {
                const responsibleLegalPk = resCreateResponsibleLegal.data.data.pk;
                const linkResponse = await fetchData(
                    "patient_responsible_legal",
                    "POST",
                    true,
                    {
                        patient_pk: patientPk,
                        responsible_legal_pk: responsibleLegalPk
                    }
                );
                return linkResponse.status === HTTP_STATUS.CREATED_201;
            }
            return false;
        } catch (error) {
            console.error("Error creating responsible legal:", error);
            return false;
        }
    };

    const finishAndClose = useCallback(() => {
        setShowPreviewModal(false);
        Notifications.toast(
            intl.get("PatientCreatedSuccess").d("Patient registered successfully!"),
            Notifications.TYPE_SUCCESS
        );
        if (table && table.current) {
            table.current.updateTable();
        }
        onClose();
    }, [table, onClose]);

    const handlePregnancyModalClose = useCallback(() => {
        setShowPregnancyPotencialModal(false);
        finishAndClose();
    }, [finishAndClose]);

    const handlePregnancyModalFinish = useCallback(() => {
        setShowPregnancyPotencialModal(false);
        finishAndClose();
    }, [finishAndClose]);

    const handleOpenPreview = useCallback(() => {
        if (!isFormValid) {
            let errorMessage = intl.get("FillRequiredFields").d("Please fill in all required fields");

            // Specific message for missing infertility reason
            if (selectedGenderOption === getGenderEnum().F.value && !hasPregnancyPotential && !selectedInfertilityReason) {
                errorMessage = intl.get("InfertilityReasonRequired").d("Please select an infertility reason");
            }

            Notifications.toast(errorMessage, Notifications.TYPE_ERROR);
            return;
        }
        setShowPreviewModal(true);
    }, [isFormValid, selectedGenderOption, hasPregnancyPotential, selectedInfertilityReason]);

    const handleClosePreview = useCallback(() => {
        setShowPreviewModal(false);
    }, []);

    const handleSave = async () => {
        if (!isFormValid) return;

        setIsSubmitting(true);
        try {
            const birthDay = dayjs(startDate).startOf("day").toISOString();
            const phoneClean = phone?.replace(/\D/g, "") || "";
            const cpf = cpfValue.replace(/\D/g, "");

            const patientPayload = {
                has_pregnancy_potential: hasPregnancyPotential,
                menstrual_cycle_regular: menstrualCycleRegular,
                status: PatientStatusEnum.ACTIVE,
                name: patientName,
                cpf: cpf,
                gender: selectedGenderOption,
                email: email,
                phone: phoneClean,
                birth_date: birthDay,
                rg: rg || null,
                rg_issuing_agency: rgIssuingAgency || null,
                data_usage_consent_accepted: dataUsageConsentAccepted,
                contact_authorization: contactAuthorization,
                address: {
                    address_name: "P_" + cpf,
                    zip_code: zipCode,
                    city: city,
                    state: state,
                    address: address,
                    number: addressNumber,
                    country_code: 'BR',
                }
            };

            const res = await fetchData("/patient", "POST", true, patientPayload);

            if (res.status !== HTTP_STATUS.CREATED_201) {
                let errorMessage = intl.get("PatientCreateError").d("Error registering patient");

                if (res.status === 409) {
                    errorMessage = intl.get("PatientCpfAlreadyExists").d("CPF already registered in the system");
                } else if (typeof res.data?.detail === 'string') {
                    if (res.data.detail.includes('UniqueViolation') || res.data.detail.includes('duplicate key')) {
                        errorMessage = intl.get("PatientAlreadyExists").d("Patient with this data already exists");
                    } else {
                        errorMessage = res.data.detail;
                    }
                }

                Notifications.toast(errorMessage, Notifications.TYPE_ERROR);
                setIsSubmitting(false);
                return;
            }

            const patientPk = res.data?.data?.pk || res.data?.pk;

            if (!patientPk) {
                Notifications.toast(
                    intl.get("PatientCreateError").d("Error registering patient"),
                    Notifications.TYPE_ERROR
                );
                setIsSubmitting(false);
                return;
            }

            setCreatedPatientPk(patientPk);
            await addPatientToDoctorList(patientPk);

            if (hasResponsibleLegal) {
                const responsibleCreated = await createResponsibleLegal(patientPk);
                if (!responsibleCreated) {
                    Notifications.toast(
                        intl.get("ResponsibleLegalCreateError").d("Error registering responsible legal, but patient was created"),
                        Notifications.TYPE_WARNING
                    );
                }
            }

            // Save contraceptive methods if patient has pregnancy potential (fertile)
            if (hasPregnancyPotential) {
                try {
                    // Combine all selected methods (highly effective + barrier)
                    const allSelectedMethods = [...highlyEffectiveMethods, ...barrierMethods];

                    // Save each contraceptive method
                    for (const contraceptiveMethodPk of allSelectedMethods) {
                        await fetchData("/patient_contraceptive_method", "POST", true, {
                            patient_pk: patientPk,
                            contraceptive_method_pk: contraceptiveMethodPk
                        });
                    }
                } catch (error) {
                    console.error("Error saving contraceptive methods:", error);
                }
            }

            // Save infertility reason if patient does not have pregnancy potential (infertile)
            if (selectedGenderOption === 'F' && !hasPregnancyPotential && selectedInfertilityReason) {
                try {
                    await fetchData("/patient_infertility_reason", "POST", true, {
                        patient_pk: patientPk,
                        infertility_reason_pk: selectedInfertilityReason
                    });
                } catch (error) {
                    console.error("Error saving infertility reason:", error);
                }
            }

            finishAndClose();
        } catch (error) {
            console.error("Error creating patient:", error);
            Notifications.toast(
                intl.get("PatientCreateError").d("Error registering patient"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetStates = useCallback(() => {
        setHasPregnancyPotential(false);
        setMenstrualCycleRegular(false);
        setHasResponsibleLegal(false);
        setSameAddressAsPatient(false);
        setShowPregnancyPotencialModal(false);
        setCpfIsValid(false);
        setCpfValue(initialCpf || '');
        setSelectedGenderOption("");
        setStartDate(initialDob ? dayjs(initialDob) : null);
        setCreatedPatientPk(null);
        setPatientName(initialName || '');
        setZipCode('');
        setCity('');
        setState('');
        setAddress('');
        setAddressNumber('');
        setPhone('');
        setEmail('');
        setRg('');
        setRgIssuingAgency('');
        setResponsibleCpfIsValid(false);
        setResponsibleCpfValue('');
        setResponsibleBirthDate(null);
        setResponsibleName('');
        setResponsibleZipCode('');
        setResponsibleCity('');
        setResponsibleState('');
        setResponsibleAddress('');
        setResponsibleAddressNumber('');
        setResponsiblePhone('');
        setResponsibleEmail('');
        setResponsibleRg('');
        setResponsibleRgIssuingAgency('');
        setSelectedContraceptiveMethod(null);
        setSelectedContraceptiveMethodName('');
        setHighlyEffectiveMethods([]);
        setBarrierMethods([]);
        setSelectedInfertilityReason(undefined);
        setSelectedInfertilityReasonName('');
        setDataUsageConsentAccepted(false);
        setContactAuthorization(null);
        setIsSubmitting(false);
        setShowPreviewModal(false);
    }, [initialCpf, initialDob, initialName]);

    return {
        state: {
            hasPregnancyPotential,
            menstrualCycleRegular,
            hasResponsibleLegal,
            sameAddressAsPatient,
            showPregnancyPotencialModal,
            cpfIsValid,
            cpfValue,
            selectedGenderOption,
            startDate,
            createdPatientPk,
            patientName,
            zipCode,
            city,
            state,
            address,
            addressNumber,
            phone,
            email,
            rg,
            rgIssuingAgency,
            responsibleCpfIsValid,
            responsibleCpfValue,
            responsibleBirthDate,
            responsibleName,
            responsibleZipCode,
            responsibleCity,
            responsibleState,
            responsibleAddress,
            responsibleAddressNumber,
            responsiblePhone,
            responsibleEmail,
            responsibleRg,
            responsibleRgIssuingAgency,
            selectedContraceptiveMethod,
            selectedContraceptiveMethodName,
            highlyEffectiveMethods,
            barrierMethods,
            selectedInfertilityReason,
            selectedInfertilityReasonName,
            dataUsageConsentAccepted,
            contactAuthorization,
            isSubmitting,
            isFormValid,
            validationErrors,
            fieldErrors,
            showPreviewModal,
        },
        handlers: {
            setHasPregnancyPotential,
            setMenstrualCycleRegular,
            setHasResponsibleLegal,
            setSameAddressAsPatient,
            setCpfIsValid,
            setCpfValue,
            setSelectedGenderOption,
            setStartDate,
            setPatientName,
            setZipCode,
            setCity,
            setState,
            setAddress,
            setAddressNumber,
            setPhone,
            setEmail,
            setRg,
            setRgIssuingAgency,
            setResponsibleCpfIsValid,
            setResponsibleCpfValue,
            setResponsibleBirthDate,
            setResponsibleName,
            setResponsibleZipCode,
            setResponsibleCity,
            setResponsibleState,
            setResponsibleAddress,
            setResponsibleAddressNumber,
            setResponsiblePhone,
            setResponsibleEmail,
            setResponsibleRg,
            setResponsibleRgIssuingAgency,
            setSelectedContraceptiveMethod,
            setSelectedContraceptiveMethodName,
            setHighlyEffectiveMethods,
            setBarrierMethods,
            setSelectedInfertilityReason,
            setSelectedInfertilityReasonName,
            setDataUsageConsentAccepted,
            setContactAuthorization,
            handleSave,
            resetStates,
            handlePregnancyModalClose,
            handlePregnancyModalFinish,
            handleOpenPreview,
            handleClosePreview,
        }
    };
};
